import { Select } from 'components/Form'
import { SelectOption } from 'components/Form/GenericSelect'
import { ChangeEvent, useEffect, useState } from 'react'
import { Box, Flex } from 'theme-ui'
import { TimeComponent } from 'types'
import { noop } from 'utils'
import {
  msFromTimeString,
  formatTime,
  MIN_IN_MS,
  timeStringFromMS,
  timeStringFromComponent,
  componentsFromTimeString,
  roundToNearest,
} from 'utils/helpers'
import { AdvancedInputProps } from '.'
import { BasicInputGroup } from './BasicInputGroup'

export interface TimeInputProps extends AdvancedInputProps {
  value?: string | null
  showErrorText?: boolean
  tabIndex: number
}

const TIME_MIN_STEP = 5
const mins = [] as SelectOption[]
for (let min = 0; min < 60; min += TIME_MIN_STEP) {
  mins.push({
    value: String(min),
    label: String(min).length < 2 ? '0' + min : String(min),
  })
}

const TIME_HOUR_STEP = 1
const hours = [] as SelectOption[]
for (let hour = 1; hour <= 12; hour += TIME_HOUR_STEP) {
  hours.push({
    value: String(hour),
    label: String(hour),
  })
}

const ampmOptions = [
  { value: 'am', label: 'AM' },
  { value: 'pm', label: 'PM' },
]

const selectContainerStyle = {
  width: 52,
  display: 'inline-block',
}

const selectPlaceholderStyle = {
  color: 'lightGray',
  fontSize: 14,
  px: '4px',
}

const selectStyle = {
  cursor: 'pointer',
  minWidth: 0,
  px: '8px',
}

export const TimeInput = ({
  name,
  label,
  setFieldValue,
  onBlur = noop,
  disabled,
  error,
  showErrorText = true,
  value,
  tabIndex,
}: TimeInputProps) => {
  const timeMS = msFromTimeString(value)
  const roundedTimeMS = roundToNearest(timeMS, TIME_MIN_STEP * MIN_IN_MS) //round to nearest 5 minute mark

  const [{ hours: hh, minutes: mm, partOfDay: ampm }, setTime] = useState(
    {} as TimeComponent
  )

  //keep state in sync with parent component
  useEffect(() => {
    const { hours, minutes, partOfDay } = componentsFromTimeString(
      timeStringFromMS(roundedTimeMS)
    )
    setTime({
      hours,
      minutes,
      partOfDay,
    })
  }, [roundedTimeMS])

  //update parent component when user updates time
  const onTimeChange = ({
    hours = hh,
    minutes = mm,
    partOfDay = ampm,
  }: TimeComponent) => {
    if (!partOfDay) partOfDay = 'am'
    const formattedTimeString = timeStringFromComponent({
      hours,
      minutes,
      partOfDay,
    })
    if (formattedTimeString) {
      setFieldValue(name, formattedTimeString)
    }
  }

  const setHour = (evt: ChangeEvent<HTMLSelectElement>) => {
    const { value: hourString } = evt.target
    const hours = Number(hourString)
    setTime((time) => ({ ...time, hours }))
    onTimeChange({ hours })
  }
  const setMin = (evt: ChangeEvent<HTMLSelectElement>) => {
    const { value: minString } = evt.target
    const minutes = Number(minString)
    setTime((time) => ({ ...time, minutes }))
    onTimeChange({ minutes })
  }
  const setAmPm = (evt: ChangeEvent<HTMLSelectElement>) => {
    const { value: ampm } = evt.target
    const partOfDay = ampm as TimeComponent['partOfDay']
    setTime((time) => ({ ...time, partOfDay }))
    onTimeChange({ partOfDay })
  }

  const displayText =
    roundedTimeMS === undefined ? '-' : formatTime(roundedTimeMS)

  const handleTimeBlur = (evt: any) => {
    evt.target.name = name
    onBlur(evt)
  }

  return (
    <BasicInputGroup
      name={name + '-hours'}
      label={label}
      disabled={disabled}
      display={displayText}
      error={showErrorText ? error : undefined}
      input={
        <Box>
          <Box sx={selectContainerStyle}>
            <SelectWithPlaceholder
              id={name + '-hours'}
              name={name + '-hours'}
              data-testid={name + '-hours'}
              value={hh !== undefined ? String(hh) : undefined}
              selectSx={{
                borderBottomRightRadius: 0,
                borderTopRightRadius: 0,
              }}
              chevronSx={{ ml: -17 }}
              disabled={disabled}
              aria-invalid={!!error}
              tabIndex={tabIndex}
              onBlur={handleTimeBlur}
              onChange={setHour}
              placeholder="HH"
              selectOptions={hours}
            />
          </Box>
          <Box
            sx={{
              ...selectContainerStyle,
              position: 'relative',
            }}>
            <Flex
              sx={{
                position: 'absolute',
                height: '100%',
                alignItems: 'center',
                left: '-2px',
              }}>
              :
            </Flex>
            <SelectWithPlaceholder
              id={name + '-mins'}
              name={name + '-mins'}
              data-testid={name + '-mins'}
              value={mm !== undefined ? String(mm) : undefined}
              selectSx={{
                borderRadius: 0,
              }}
              chevronSx={{ ml: -15 }}
              aria-invalid={!!error}
              disabled={disabled}
              tabIndex={tabIndex}
              onBlur={handleTimeBlur}
              onChange={setMin}
              placeholder="MM"
              selectOptions={mins}
            />
          </Box>

          <Box sx={selectContainerStyle}>
            <SelectWithPlaceholder
              id={name + '-ampm'}
              name={name + '-ampm'}
              data-testid={name + '-ampm'}
              value={ampm}
              selectSx={{
                borderBottomLeftRadius: 0,
                borderTopLeftRadius: 0,
              }}
              chevronSx={{ ml: -18 }}
              aria-invalid={!!error}
              disabled={disabled ?? false}
              tabIndex={tabIndex}
              onBlur={handleTimeBlur}
              onChange={setAmPm}
              placeholder="AM"
              selectOptions={ampmOptions}
            />
          </Box>
        </Box>
      }
    />
  )
}

interface SelectWithPlaceholderProps extends Partial<HTMLSelectElement> {
  value?: string
  placeholder?: string
  selectOptions: SelectOption[]
  selectSx?: Object
  chevronSx?: Object
  onBlur?: (evt: FocusEvent) => void
  onChange?: (evt: ChangeEvent<HTMLSelectElement>) => void
}
const SelectWithPlaceholder = ({
  selectSx,
  value,
  placeholder,
  selectOptions,
  ...rest
}: SelectWithPlaceholderProps) => {
  return (
    <Select
      value={String(value ?? '')}
      selectSx={{
        ...selectStyle,
        ...selectSx,
        ...(value === undefined ? selectPlaceholderStyle : {}),
      }}
      {...rest}>
      <option value="" disabled hidden>
        {placeholder}
      </option>
      {selectOptions.map(({ value, label }) => (
        <option
          style={{ color: 'black', fontSize: 16 }}
          key={value}
          value={value + ''}>
          {label}
        </option>
      ))}
    </Select>
  )
}
